import React, { useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import SonFactor, { type SonFactorProps } from 'src/shared/components/son-factor/SonFactor';
import SonCategoryModal from 'src/shared/components/son-category-modal/SonCategoryModal';
import type { ScoreBand } from 'src/shared/components/risk-score-cell/riskScoreColour';

type FactorItem = { name: string; sourceKey: string; weight: number } & (
    | { score: number | null; band?: never }
    | { band: ScoreBand; score?: never }
);

export type SonFactorsPanelProps = {
    title: string;
    slug: string;
    factors: FactorItem[];
    isLoading?: boolean;
};

const SonFactorsPanel = ({ title, slug, factors, isLoading = false }: SonFactorsPanelProps) => {
    const [modalOpen, setModalOpen] = useState(false);

    return (
        <div className='bg-white shadow-portfolio-summary-card rounded-lg py-3 divide-y flex flex-col gap-3'>
            <div className='flex items-center gap-2 px-4 xl:px-5'>
                <span className='text-[16px] font-normal text-primary'>{title}</span>
                <button className='border border-border rounded-lg px-2 py-0.5 text-xs text-foreground' onClick={() => setModalOpen(true)}>
                    Learn more
                </button>
            </div>

            {isLoading ? (
                <div className='flex flex-col gap-3 animate-pulse px-4 xl:px-5 pt-2'>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className='flex items-center justify-between'>
                            <div className='h-4 bg-grey-20 rounded w-1/3' />
                            <div className='h-2 bg-grey-20 rounded w-1/2' />
                            <div className='h-4 bg-grey-20 rounded w-20' />
                        </div>
                    ))}
                </div>
            ) : factors.length === 0 ? (
                <div className='flex items-center justify-center h-24 text-sm text-grey-60 px-4 xl:px-5 pt-2'>No factors found</div>
            ) : (
                <div className='px-4 xl:px-5 pt-2'>
                    {factors.map(factor => (
                        <SonFactor key={factor.name} {...(factor as SonFactorProps)} />
                    ))}
                </div>
            )}

            {modalOpen && (
                <Modal
                    onClose={() => setModalOpen(false)}
                    dismissOnClickOutside
                    classNames={{ body: 'max-h-[90vh] w-[90vw] max-w-[1000px]' }}
                >
                    <SonCategoryModal
                        title={title}
                        categorySlug={`son_${slug}`}
                        datasets={factors.map(f => ({ name: f.name, sourceKey: f.sourceKey, weight: f.weight }))}
                    />
                </Modal>
            )}
        </div>
    );
};

export default SonFactorsPanel;
